/**
 * v2 Home — chassis list, the default landing.
 *
 * Goals:
 * - **P1**: chassis bracketed under their Gigafy location with a
 *   header per group; locations sorted alphabetically, chassis
 *   within each location sorted by ``gigafy_device_name``.
 * - **P2**: per-chassis unacknowledged-alarm badges (critical /
 *   major / minor) so operators spot brewing issues at-a-glance.
 *
 * Data:
 *   - ``GET /api/chassis`` — fleet rows (existing endpoint)
 *   - ``GET /gigafy/locations`` — location-name lookup
 *   - ``GET /api/alarms/count-by-chassis`` — P2 severity breakdown
 *
 * The three queries are independent and run in parallel via
 * TanStack Query.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { LocalTime } from "../../lib/datetime";
import { Badge } from "../components/Badge";
import { FilterBar } from "../components/FilterBar";
import { useToast } from "../components/Toast";
import {
  VdslErrorCountsPills,
  type VdslErrorHealthCounts,
} from "../components/VdslErrorCountsPills";
import { HelpButton } from "../help/HelpPanel";

/** Inline quick-job buttons on each chassis card. Matches v1's
 *  ``apps/frontend/src/pages/Chassis.tsx`` array — same six
 *  job names + labels — so the v1 → v2 cutover doesn't drop a
 *  workflow. */
const QUICK_JOBS: ReadonlyArray<{ name: string; label: string }> = [
  { name: "collect_optical", label: "Optical" },
  { name: "list_onus", label: "ONUs" },
  { name: "list_pending_onus", label: "Pending" },
  { name: "parse_alarms", label: "Alarms" },
  { name: "collect_mac", label: "MAC" },
  { name: "backup_running_config", label: "Backup" },
];

/** Vendor-specific quick-job allowlist. Adapter capability flags
 *  live in the worker (libs/chassis_adapters/...adapter.capabilities)
 *  but the frontend doesn't fetch them, so we mirror the supported
 *  set here. When a new adapter ships with a non-default capability
 *  set, add an entry; otherwise the card shows the full bar.
 *
 *  Without this filter, hitting "Optical" on an Ubiquiti chassis
 *  dispatches ``collect_optical`` → adapter raises NotImplementedError
 *  → job fails noisily. The dispatcher arms are vendor-aware below
 *  the adapter; the UI used to assume every adapter implements all
 *  six. */
const ADAPTER_QUICK_JOBS: Record<string, ReadonlySet<string>> = {
  "ubiquiti:gpon": new Set(["list_onus", "collect_mac", "collect_optical"]),
  "calix:gpon": new Set(["list_onus", "collect_mac", "collect_optical"]),
};

function quickJobsFor(adapterKey: string): ReadonlyArray<{ name: string; label: string }> {
  const allow = ADAPTER_QUICK_JOBS[adapterKey];
  if (!allow) return QUICK_JOBS;
  return QUICK_JOBS.filter((j) => allow.has(j.name));
}

type ChassisRow = {
  id: number;
  gigafy_device_id: string | null;
  gigafy_device_name: string | null;
  device_name_override: string | null;
  effective_device_name: string;
  gigafy_location_id: string | null;
  adapter_key: string;
  technology: string;
  host_synced: string | null;
  host_override: string | null;
  effective_host: string | null;
  effective_port: number;
  enabled: boolean;
  last_seen_at: string | null;
};

type ChassisListResponse = { chassis: ChassisRow[] };

type HealthCounts = {
  good: number;
  warning: number;
  critical: number;
  unknown: number;
  total: number;
  // VDSL chassis only — line error-health flags (migration 0036).
  vdsl_errors?: VdslErrorHealthCounts | null;
};

type HealthSummaryResponse = {
  counts: Record<string, HealthCounts>;
};

type PendingSummaryResponse = {
  counts: Record<string, number>;
};

type LocationRow = {
  entity_id: string;
  name: string;
  address: string | null;
};

type LocationsResponse = { locations: LocationRow[] };

type AlarmCount = {
  chassis_id: number;
  critical: number;
  major: number;
  minor: number;
  warning: number;
  info: number;
};

type AlarmCountResponse = { counts: AlarmCount[] };

type Group = {
  location_id: string | null;
  location_name: string;
  chassis: ChassisRow[];
};

export function Home() {
  const [q, setQ] = useState("");

  const chassisQ = useQuery<ChassisListResponse>({
    queryKey: ["v2", "chassis-list"],
    queryFn: async () => {
      const res = await api.get<ChassisListResponse>("/chassis");
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const locationsQ = useQuery<LocationsResponse>({
    queryKey: ["v2", "gigafy-locations"],
    queryFn: async () => {
      const res = await api.get<LocationsResponse>("/gigafy/locations");
      return res.data;
    },
    // Locations change rarely; cache hard.
    staleTime: 5 * 60_000,
  });

  // P2 — per-chassis severity breakdown. Refetch every 30s
  // alongside the chassis list so the badges stay current.
  const alarmsQ = useQuery<AlarmCountResponse>({
    queryKey: ["v2", "alarm-count-by-chassis"],
    queryFn: async () => {
      const res = await api.get<AlarmCountResponse>(
        "/alarms/count-by-chassis",
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  // v0.14.1 — per-chassis health summary (ONU optical bands for
  // GPON, line-speed bands for VDSL). Same backend SQL as the
  // location-devices-list pills.
  const healthQ = useQuery<HealthSummaryResponse>({
    queryKey: ["v2", "chassis-health-summary"],
    queryFn: async () => {
      const res = await api.get<HealthSummaryResponse>(
        "/chassis/health-summary",
      );
      return res.data;
    },
    refetchInterval: 60_000,
  });

  // Per-chassis pending-ONU count → "N pending" badge so operators
  // spot chassis with discovered-but-unprovisioned hardware waiting.
  const pendingQ = useQuery<PendingSummaryResponse>({
    queryKey: ["v2", "chassis-pending-summary"],
    queryFn: async () => {
      const res = await api.get<PendingSummaryResponse>(
        "/chassis/pending-summary",
      );
      return res.data;
    },
    refetchInterval: 60_000,
  });

  // Build a quick lookup: location_id → name, chassis_id → severity.
  const locationName = useMemo(() => {
    const m = new Map<string, string>();
    for (const loc of locationsQ.data?.locations ?? []) {
      m.set(loc.entity_id, loc.name);
    }
    return m;
  }, [locationsQ.data]);

  const alarmCount = useMemo(() => {
    const m = new Map<number, AlarmCount>();
    for (const a of alarmsQ.data?.counts ?? []) {
      m.set(a.chassis_id, a);
    }
    return m;
  }, [alarmsQ.data]);

  // P1 — group + sort.
  const groups = useMemo<Group[]>(() => {
    const all = chassisQ.data?.chassis ?? [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? all.filter(
          (c) =>
            (c.gigafy_device_name ?? "").toLowerCase().includes(needle) ||
            (c.effective_host ?? "").toLowerCase().includes(needle) ||
            c.adapter_key.toLowerCase().includes(needle) ||
            (locationName.get(c.gigafy_location_id ?? "") ?? "")
              .toLowerCase()
              .includes(needle),
        )
      : all;
    // Group by location_id (null falls under "(no location)").
    const buckets = new Map<string | null, ChassisRow[]>();
    for (const c of filtered) {
      const key = c.gigafy_location_id ?? null;
      const arr = buckets.get(key);
      if (arr) arr.push(c);
      else buckets.set(key, [c]);
    }
    return Array.from(buckets.entries())
      .map<Group>(([location_id, chassis]) => ({
        location_id,
        location_name: location_id
          ? locationName.get(location_id) ?? `Location ${location_id.slice(0, 8)}…`
          : "(no location)",
        chassis: chassis.sort((a, b) =>
          (a.gigafy_device_name ?? `chassis-${a.id}`).localeCompare(
            b.gigafy_device_name ?? `chassis-${b.id}`,
          ),
        ),
      }))
      .sort((a, b) => {
        // "(no location)" always last.
        if (a.location_id === null) return 1;
        if (b.location_id === null) return -1;
        return a.location_name.localeCompare(b.location_name);
      });
  }, [chassisQ.data, locationName, q]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 animate-enter">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Chassis</h1>
          <HelpButton topic="home" />
        </div>
        <FilterBar
          value={q}
          onChange={setQ}
          placeholder="Filter by name, host, adapter, location…"
        />
      </div>
      {chassisQ.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-slate-500">
          {chassisQ.data?.chassis.length === 0
            ? "No chassis adopted yet. Open Locations to adopt one."
            : "No chassis matches that filter."}
        </p>
      ) : (
        <div className="flex flex-col gap-4 animate-enter [animation-delay:80ms]">
          {groups.map((group) => (
            <div key={group.location_id ?? "no-loc"} className="flex flex-col gap-2">
              <h2 className="flex items-baseline gap-2 text-sm font-medium uppercase tracking-wide text-slate-400">
                {group.location_name}
                <span className="text-[10px] text-slate-500">
                  · {group.chassis.length} chassis
                </span>
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.chassis.map((c) => (
                  <ChassisCard
                    key={c.id}
                    chassis={c}
                    alarms={alarmCount.get(c.id)}
                    health={healthQ.data?.counts[String(c.id)]}
                    pending={pendingQ.data?.counts[String(c.id)] ?? 0}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ChassisCard({
  chassis,
  alarms,
  health,
  pending = 0,
}: {
  chassis: ChassisRow;
  alarms?: AlarmCount;
  health?: HealthCounts;
  pending?: number;
}) {
  const toast = useToast();
  const submit = useMutation({
    mutationFn: async (jobName: string) => {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassis.id}/jobs`,
        { name: jobName, params: {} },
      );
      return { jobId: res.data.id, jobName };
    },
    onSuccess: ({ jobId, jobName }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId, kind: jobName },
        }),
      );
      toast.push(`${jobName} queued`, "success");
    },
    onError: () => toast.push("Job submit failed", "error"),
  });
  // Stop click events on interactive children from bubbling to the
  // wrapping <Link>. Without this, clicking a "Quick run" button
  // would navigate to /chassis/<id> as well as fire the button.
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <Link
      to={`/chassis/${chassis.id}`}
      className="group flex flex-col gap-1.5 rounded-lg bg-slate-900 shadow-border p-3 transition-[box-shadow,background-color] duration-150 ease-out hover:bg-slate-900/80 hover:shadow-border-hover"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-100 group-hover:text-emerald-200">
          {chassis.effective_device_name}
        </span>
        <span className="flex items-center gap-1.5">
          {pending > 0 && (
            <Badge
              tone="warning"
              title={`${pending} pending ONU${pending === 1 ? "" : "s"} awaiting provisioning`}
            >
              ⚠ {pending} pending
            </Badge>
          )}
          <Badge tone="info">{chassis.adapter_key}</Badge>
        </span>
      </div>
      <div className="text-xs text-slate-500">
        {chassis.effective_host ?? "no host"}:{chassis.effective_port}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-slate-500">
        {chassis.enabled ? (
          <span className="rounded bg-emerald-900/40 px-1.5 py-0.5 text-emerald-200">
            enabled
          </span>
        ) : (
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-slate-400">
            disabled
          </span>
        )}
        {chassis.last_seen_at && (
          <span>
            last seen <LocalTime iso={chassis.last_seen_at} />
          </span>
        )}
      </div>
      {alarms && (alarms.critical + alarms.major + alarms.minor) > 0 && (
        // P2 — severity badges only show when there's something to
        // act on. Critical and major use the danger tone so they
        // catch the eye; minor is warning.
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {alarms.critical > 0 && (
            <Badge tone="danger" title="unacknowledged critical alarms">
              {alarms.critical} critical
            </Badge>
          )}
          {alarms.major > 0 && (
            <Badge tone="danger" title="unacknowledged major alarms">
              {alarms.major} major
            </Badge>
          )}
          {alarms.minor > 0 && (
            <Badge tone="warning" title="unacknowledged minor alarms">
              {alarms.minor} minor
            </Badge>
          )}
        </div>
      )}
      {/* v0.14.1 — health pills (ONU for GPON, line-speed for VDSL).
          Same band semantics as the location-devices-list pills. */}
      {health && health.total > 0 ? (
        <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px]">
          <span
            className="inline-flex items-center gap-1 text-emerald-400"
            title={
              chassis.technology === "vdsl"
                ? "VDSL ports >80 Mbps"
                : "ONUs with healthy RX power"
            }
          >
            <span aria-hidden>●</span>
            <span className="text-slate-200">{health.good}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-amber-400">
            <span aria-hidden>●</span>
            <span className="text-slate-200">{health.warning}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-red-500">
            <span aria-hidden>●</span>
            <span className="text-slate-200">{health.critical}</span>
          </span>
          {health.unknown > 0 ? (
            <span
              className="inline-flex items-center gap-1 text-slate-500"
              title="Inventory entries with no recent sample"
            >
              <span aria-hidden>●</span>
              <span className="text-slate-300">{health.unknown}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      {/* VDSL line-error flags (7-day counters) — alongside the rate bands. */}
      {health?.vdsl_errors &&
        health.vdsl_errors.warning + health.vdsl_errors.critical > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-[11px]">
            <VdslErrorCountsPills counts={health.vdsl_errors} />
          </div>
        )}
      {/* Quick-job row. Mirrors v1's /chassis page so operators
          don't lose the fleet-wide diagnostics workflow after the
          v0.10.0 v1 retirement. Submits hand off to the inline
          job tracker via the standard custom event. */}
      <div className="mt-1 flex flex-wrap gap-1 border-t border-slate-800/60 pt-2">
        {quickJobsFor(chassis.adapter_key).map((job) => (
          <button
            key={job.name}
            type="button"
            onClick={(e) => {
              stop(e);
              submit.mutate(job.name);
            }}
            disabled={!chassis.enabled || submit.isPending}
            className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            title={`Submit ${job.name} on this chassis`}
          >
            {job.label}
          </button>
        ))}
      </div>
    </Link>
  );
}
